import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject, isDevMode } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';

/** Scripts written right to left, as ISO 15924 codes */
const RIGHT_TO_LEFT_SCRIPTS = new Set(['Adlm', 'Arab', 'Hebr', 'Nkoo', 'Rohg', 'Syrc', 'Thaa']);

/**
 * The BCP 47 language tag for a Transloco language key, in canonical case (`zh-hant` becomes
 * `zh-Hant`), or undefined when the key is not a valid tag.
 */
export function languageTag(languageKey: string): string | undefined {
  try {
    return Intl.getCanonicalLocales(languageKey)[0];
  } catch {
    return undefined;
  }
}

/** The direction of a language's script, for the `dir` attribute */
export function textDirection(tag: string): 'ltr' | 'rtl' {
  const script = new Intl.Locale(tag).maximize().script;
  return script && RIGHT_TO_LEFT_SCRIPTS.has(script) ? 'rtl' : 'ltr';
}

/**
 * Keeps `<html lang>` and `<html dir>` in step with Transloco's active language, whoever changes
 * it. Screen readers, hyphenation, spell-check, browser translation and CJK glyph selection read
 * them.
 */
@Injectable({ providedIn: 'root' })
export class DocumentLanguageService {
  private readonly document = inject(DOCUMENT);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private started = false;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((languageKey) => this.applyLanguage(languageKey));
  }

  private applyLanguage(languageKey: string): void {
    const tag = languageTag(languageKey);
    if (!tag) {
      if (isDevMode()) {
        console.warn(`DocumentLanguageService: "${languageKey}" is not a BCP 47 language tag; <html lang> left unchanged`);
      }
      return;
    }
    const root = this.document.documentElement;
    root.setAttribute('lang', tag);
    root.setAttribute('dir', textDirection(tag));
  }
}
